import React, { useState, useEffect, useRef } from "react";
import {
  FiUsers,
  FiAlertCircle,
  FiCheckCircle,
  FiBell,
  FiArrowUp,
  FiArrowDown,
  FiChevronDown,
  FiX,
  FiActivity,
  FiZap,
  FiTrendingDown,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import CountUp from "react-countup";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import WellbeingAnalysis from "./WellbeingAnalysis";
import Benchmarking from "./Benchmarking";
import SchoolOrbit from "./SchoolOrbit";
import { useAuth } from "../../context/AuthContext";
import { getZoneColor } from "../../utils/zoneUtils";

interface AnalyticsData {
  ensightlyScore: {
    current: number;
    comp1: { score: number; label: string };
    comp2: { score: number; label: string };
  };
  zones: {
    current: any;
    comp1: { data: any; label: string };
    comp2: { data: any; label: string };
  };
  attentionAreas: Array<{ category: string; score: number; lastMonthScore: number | null; change: number | null }>;
  hotspots: Array<{ class: string; score: number; category: string; trend: number }>;
  gradeHotspots: Array<{ grade: string; category: string; score: number; trend: number }>;
  overallTrend: Array<{ month: string; score: number | null }>;
  overallTrendByBoard: Array<{ month: string; [key: string]: any }>;
  boardNames: string[];
  aiInsights: string[];
}

interface SchoolStats {
  totalStudents: number;
  totalTeachers: number;
  totalClasses: number;
  activeStudents: number;
  studentsNeedingAttention: number;
  criticalCases: number;
  criticalCasesChange: number;
  averageAttendance: number;
  moodTrackingActive: number;
  studentsLoggedInMonth: number;
  avgLogsPerStudent: number;
  dailyActivePct: number;
}


// Custom Premium Select Component
const CustomSelect = ({ 
  label, 
  value, 
  options, 
  onChange, 
  colorTheme = "primary" 
}: { 
  label: string; 
  value: string; 
  options: {value: string, label: string}[]; 
  onChange: (val: string) => void;
  colorTheme?: "primary" | "gray";
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(o => o.value === value) || options[0];

  const themeColors = {
    primary: {
      label: "text-[#1ecab8]",
      bgHover: "hover:bg-[#1ecab8]/5",
      borderHover: "hover:border-[#1ecab8]/30",
      borderFocus: "focus:border-[#1ecab8]",
      ringFocus: "focus:ring-[#1ecab8]/10",
      activeText: "text-[#1ecab8]",
      activeBg: "bg-[#1ecab8]/10",
      bgMain: "bg-white",
    },
    gray: {
      label: "text-gray-400",
      bgHover: "hover:bg-gray-50",
      borderHover: "hover:border-gray-100",
      borderFocus: "focus:border-gray-400",
      ringFocus: "focus:ring-gray-100",
      activeText: "text-gray-900",
      activeBg: "bg-gray-100",
      bgMain: "bg-gray-50/50",
    }
  };
  const theme = themeColors[colorTheme];

  return (
    <div className="relative group" ref={dropdownRef}>
      <label className={`absolute -top-2.5 left-3 px-1 bg-white text-[10px] font-bold uppercase tracking-wider z-10 rounded ${theme.label}`}>
        {label}
      </label>
      <div 
        className={`relative flex items-center justify-between w-[200px] pl-4 pr-3 py-3 ${theme.bgMain} border border-gray-100 ${theme.borderHover} rounded-xl text-sm font-medium text-gray-700 outline-none ${theme.ringFocus} transition-all cursor-pointer shadow-sm`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">{selectedOption?.label}</span>
        <FiChevronDown size={18} className={`text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.12)] py-1.5 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              className={`px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${value === opt.value ? theme.activeBg + ' ' + theme.activeText : 'text-gray-600 ' + theme.bgHover}`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SchoolDashboard: React.FC = () => {
  const { user } = useAuth();
  // Only use user.id as schoolId if this is a school-type user, not an admin
  const schoolId = user?.schoolId || (user?.userType === 'school' ? user?.id : undefined);

  const [stats, setStats] = useState<SchoolStats>({
    totalStudents: 0,
    totalTeachers: 0,
    totalClasses: 0,
    activeStudents: 0,
    studentsNeedingAttention: 0,
    criticalCases: 0,
    criticalCasesChange: 0,
    averageAttendance: 0,
    moodTrackingActive: 0,
    studentsLoggedInMonth: 0,
    avgLogsPerStudent: 0,
    dailyActivePct: 0,
  });
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("current_month");
  const [compareMode, setCompareMode] = useState<string>("auto");
  const [customCompareStart, setCustomCompareStart] = useState<string>("");
  const [customCompareEnd, setCustomCompareEnd] = useState<string>("");
  const [selectedBoard, setSelectedBoard] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("Executive Dashboard");

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Build compare query params
        let compareParams = `period=${selectedPeriod}`;
        if (compareMode === "custom" && customCompareStart && customCompareEnd) {
          compareParams += `&compareStart=${customCompareStart}&compareEnd=${customCompareEnd}`;
        } else if (compareMode !== "auto") {
          compareParams += `&comparePreset=${compareMode}`;
        }
        if (selectedBoard !== "all") {
          compareParams += `&board=${encodeURIComponent(selectedBoard)}`;
        }
        
        let statsUrl = schoolId ? `${API_BASE_URL}/schools/dashboard/stats?schoolId=${schoolId}` : `${API_BASE_URL}/schools/dashboard/stats`;
        if (selectedBoard !== "all") {
          statsUrl += (statsUrl.includes('?') ? '&' : '?') + `board=${encodeURIComponent(selectedBoard)}`;
        }
        const analyticsUrl = schoolId ? `${API_BASE_URL}/schools/analytics/dashboard?schoolId=${schoolId}&${compareParams}` : `${API_BASE_URL}/schools/analytics/dashboard?${compareParams}`;
        
        const [statsRes, analyticsRes] = await Promise.all([
          axios.get(statsUrl),
          axios.get(analyticsUrl)
        ]);
        
        if (analyticsRes.data) {
          setAnalytics(analyticsRes.data);
        }

        const response = statsRes;
        if (response.data) {
          setStats({
            totalStudents: response.data.totalStudents || 0,
            totalTeachers: response.data.totalTeachers || 0,
            totalClasses: response.data.totalClasses || 0,
            activeStudents: response.data.activeStudents || 0,
            studentsNeedingAttention: response.data.studentsNeedingAttention || 0,
            criticalCases: response.data.criticalCases || 0,
            criticalCasesChange: response.data.criticalCasesChange || 0,
            averageAttendance: response.data.averageAttendance || 0,
            moodTrackingActive: response.data.moodTrackingActive || 0,
            studentsLoggedInMonth: response.data.studentsLoggedInMonth || 0,
            avgLogsPerStudent: response.data.avgLogsPerStudent || 0,
            dailyActivePct: response.data.dailyActivePct || 0,
          });
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedPeriod, compareMode, customCompareStart, customCompareEnd, schoolId, selectedBoard]);



  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1ecab8] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">School Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening at your school.</p>
      </div>

      {/* Filter Bar */}
      {activeTab === "Executive Dashboard" && (
      <div className="mb-8 bg-white/80 backdrop-blur-xl border border-gray-100 rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-wrap items-center gap-6 relative z-30">
        
        <div className="flex items-center gap-6 flex-1">
          {/* Period */}
          <CustomSelect
            label="View Period"
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            colorTheme="primary"
            options={[
              { value: "current_month", label: "Current Month" },
              { value: "last_month", label: "Last Month" },
              { value: "last_quarter", label: "Last Quarter" },
              { value: "year", label: "Year to Date" },
              { value: "all_time", label: "All Time" }
            ]}
          />

          <div className="h-10 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

          <CustomSelect
            label="Compare With"
            value={compareMode}
            onChange={(val) => { setCompareMode(val); setCustomCompareStart(""); setCustomCompareEnd(""); }}
            colorTheme="gray"
            options={[
              { value: "auto", label: "Auto (Smart Default)" },
              { value: "last_month", label: "Last Month" },
              { value: "last_quarter", label: "Last Quarter" },
              { value: "last_year", label: "Last Year" },
              { value: "custom", label: "Custom Date Range…" }
            ]}
          />

          <div className="h-10 w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

          {/* Board Filter */}
          <CustomSelect
            label="Board"
            value={selectedBoard}
            onChange={setSelectedBoard}
            colorTheme="primary"
            options={[
              { value: "all", label: "All Boards" },
              ...(analytics?.boardNames || []).map(b => ({ value: b, label: b }))
            ]}
          />

          {/* Custom date pickers */}
          {compareMode === "custom" && (
            <div className="flex items-center gap-3">
              <div className="relative">
                <label className="absolute -top-2.5 left-3 px-1 bg-white text-[10px] font-bold text-indigo-400 uppercase tracking-wider z-10 rounded">Compare From</label>
                <input
                  type="date"
                  value={customCompareStart}
                  onChange={(e) => setCustomCompareStart(e.target.value)}
                  className="pl-4 pr-3 py-3 bg-white border border-indigo-100 hover:border-indigo-300 rounded-xl text-sm text-gray-700 outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-400 transition-all cursor-pointer shadow-sm"
                />
              </div>
              <span className="text-gray-300 font-medium">to</span>
              <div className="relative">
                <label className="absolute -top-2.5 left-3 px-1 bg-white text-[10px] font-bold text-indigo-400 uppercase tracking-wider z-10 rounded">Compare To</label>
                <input
                  type="date"
                  value={customCompareEnd}
                  onChange={(e) => setCustomCompareEnd(e.target.value)}
                  className="pl-4 pr-3 py-3 bg-white border border-indigo-100 hover:border-indigo-300 rounded-xl text-sm text-gray-700 outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-400 transition-all cursor-pointer shadow-sm"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 border-l border-gray-100 pl-6">
          {(selectedPeriod !== "current_month" || compareMode !== "auto") && (
            <button 
              onClick={() => {
                setSelectedPeriod("current_month");
                setCompareMode("auto");
                setCustomCompareStart("");
                setCustomCompareEnd("");
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
            >
              <FiX size={14} />
              Clear
            </button>
          )}
          <button className="p-2.5 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 hover:border-gray-100 transition-all text-gray-500 hover:text-gray-700 shadow-sm">
            <FiBell size={18} />
          </button>
        </div>
      </div>
      )}

      {/* Internal Tabs */}
      <div className="flex border-b border-gray-100 mb-6">
        {["Executive Dashboard", "Wellbeing Analysis", "Benchmarking", "School Influence Benchmark"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? "border-[#1ecab8] text-[#1ecab8]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-100"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Executive Dashboard" && (
        <div className="flex flex-col gap-6">

          {/* ── MAIN CONTENT AREA ─────────────────────────────────── */}
          <div className="flex-1 min-w-0 space-y-6">

            {/* KPI Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {/* Total Students */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2.5 rounded-lg bg-[#1ecab8]/10"><FiUsers size={20} className="text-[#1ecab8]" /></div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total</span>
                </div>
                <p className="text-2xl font-black text-gray-900"><CountUp end={stats.totalStudents} duration={2} /></p>
                <p className="text-sm text-gray-500 mt-1">Total Students</p>
              </div>

              {/* Active Students */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2.5 rounded-lg bg-emerald-50"><FiCheckCircle size={20} className="text-emerald-500" /></div>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                    {stats.totalStudents > 0 ? Math.round((stats.activeStudents / stats.totalStudents) * 100) : 0}%
                  </span>
                </div>
                <p className="text-2xl font-black text-gray-900"><CountUp end={stats.activeStudents} duration={2} /></p>
                <p className="text-sm text-gray-500 mt-1">Active in Ensightly</p>
              </div>

              {/* Critical Cases */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2.5 rounded-lg bg-red-50"><FiAlertCircle size={20} className="text-red-500" /></div>
                  <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                    stats.criticalCasesChange > 0 ? "bg-red-50 text-red-600" : stats.criticalCasesChange < 0 ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"
                  }`}>
                    {stats.criticalCasesChange > 0 ? <FiArrowUp size={11} /> : stats.criticalCasesChange < 0 ? <FiArrowDown size={11} /> : null}
                    {stats.criticalCasesChange > 0 ? `+${stats.criticalCasesChange}` : stats.criticalCasesChange} vs last mo.
                  </div>
                </div>
                <p className="text-2xl font-black text-gray-900"><CountUp end={stats.criticalCases} duration={2} /></p>
                <p className="text-sm text-gray-500 mt-1">Critical Cases</p>
              </div>

              {/* Engagement Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2.5 rounded-lg bg-violet-50"><FiZap size={20} className="text-violet-500" /></div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">This Month</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Students Logged In</span>
                    <span className="text-sm font-bold text-gray-800">{stats.studentsLoggedInMonth}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Avg Logs / Student</span>
                    <span className="text-sm font-bold text-gray-800">{stats.avgLogsPerStudent}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Daily Active %</span>
                    <span className="text-sm font-bold text-violet-600">{stats.dailyActivePct}%</span>
                  </div>
                </div>
              </div>
            </div>



            {analytics && stats.totalStudents > 0 ? (
              <>
                {/* Ensightly Score - Multi-line Board Trend */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Overall School Ensightly Score</h2>
                      <p className="text-sm text-gray-500">12-month trend by board</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-3xl font-black text-[#1ecab8]">{analytics.ensightlyScore.current}</span>
                        <span className="text-sm text-gray-400 ml-1">/ 100</span>
                      </div>
                      {analytics.ensightlyScore.comp1 && (
                        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg ${
                          analytics.ensightlyScore.current >= analytics.ensightlyScore.comp1.score ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                        }`}>
                          {analytics.ensightlyScore.current >= analytics.ensightlyScore.comp1.score ? <FiArrowUp size={12}/> : <FiArrowDown size={12}/>}
                          {Math.abs(analytics.ensightlyScore.current - analytics.ensightlyScore.comp1.score)} pts {analytics.ensightlyScore.comp1.label}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="h-64 mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={(analytics.overallTrendByBoard && analytics.overallTrendByBoard.length > 0) ? analytics.overallTrendByBoard : analytics.overallTrend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9ca3af', fontSize: 11 }} domain={[0, 100]} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                        <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }} />
                        {analytics.boardNames && analytics.boardNames.length > 0 ? (
                          analytics.boardNames.map((board, idx) => {
                            const colors = ["#1ecab8", "#6366f1", "#f59e0b", "#ec4899", "#3b82f6"];
                            return (
                              <Line key={board} type="monotone" dataKey={board} stroke={colors[idx % colors.length]}
                                strokeWidth={2.5} dot={{ r: 3, strokeWidth: 0, fill: colors[idx % colors.length] }}
                                activeDot={{ r: 5 }} connectNulls name={board} />
                            );
                          })
                        ) : (
                          <Line type="monotone" dataKey="score" stroke="#1ecab8" strokeWidth={3}
                            dot={{ r: 4, fill: '#1ecab8', strokeWidth: 0 }} activeDot={{ r: 6 }} connectNulls name="Ensightly Score" />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* AI Wellbeing Insights (Emerging Trends) */}
                {analytics && analytics.aiInsights && analytics.aiInsights.length > 0 && stats.totalStudents > 0 && (
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 bg-gradient-to-br from-teal-400 to-teal-500 rounded-lg text-white shadow-sm shadow-teal-200">
                        <FiActivity size={16} />
                      </div>
                      <h3 className="text-base font-bold text-gray-900">Emerging Trends</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {analytics.aiInsights.map((insight: string, i: number) => {
                        const isAlert = insight.includes("🚨");
                        const isWarning = insight.includes("⚠️");
                        const isPositive = insight.includes("✅");
                        const clean = insight.replace(/[🚨⚠️✅📉📈]/g, '').trim();
                        
                        let icon = <FiAlertCircle size={14} />;
                        let bgClass = "bg-slate-50 border-slate-100 text-slate-700";
                        let iconClass = "text-slate-500 bg-white";
                        
                        if (isAlert) {
                          bgClass = "bg-red-50/70 border-red-100 text-red-700";
                          iconClass = "text-red-500 bg-white shadow-sm";
                          icon = <FiTrendingDown size={14} />;
                        } else if (isWarning) {
                          bgClass = "bg-amber-50/70 border-amber-100 text-amber-700";
                          iconClass = "text-amber-500 bg-white shadow-sm";
                          icon = <FiAlertCircle size={14} />;
                        } else if (isPositive) {
                          bgClass = "bg-teal-50/70 border-teal-100 text-teal-700";
                          iconClass = "text-teal-500 bg-white shadow-sm";
                          icon = <FiCheckCircle size={14} />;
                        }

                        return (
                          <div key={i} className={`px-4 py-3 rounded-lg border text-sm font-medium flex items-center gap-3 ${bgClass}`}>
                            <div className={`flex-shrink-0 p-1.5 rounded-md ${iconClass}`}>{icon}</div>
                            <p>{clean}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Zone Distribution + Attention Areas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Zone Distribution */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-base font-bold text-gray-900 mb-1">Zone Distribution</h2>
                    <p className="text-sm text-gray-500 mb-5">Current breakdown of student wellbeing zones</p>
                    <div className="space-y-4">
                      {['Green Zone', 'Blue Zone', 'Yellow Zone', 'Brown Zone', 'Red Zone', 'Dark Red'].map(zoneName => {
                        const zoneKey = zoneName.replace(' Zone', '').replace('Dark Red', 'Dark Red');
                        // Support both "Green Zone" and "Green" keys from backend
                        const val = analytics.zones.current[zoneName] ?? analytics.zones.current[zoneKey] ?? 0;
                        const comp1Val = (analytics.zones.comp1?.data || {})[zoneName] ?? (analytics.zones.comp1?.data || {})[zoneKey] ?? 0;
                        const diff = val - comp1Val;
                        const isNegativeZone = ['Brown Zone', 'Red Zone', 'Dark Red'].includes(zoneName);
                        const count = analytics.zones.current[`${zoneName}Count`] ?? analytics.zones.current[`${zoneKey}Count`] ?? 0;
                        const { color } = getZoneColor(zoneName);
                        return (
                          <div key={zoneName}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-gray-700">{zoneName}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold">{val}% <span className="text-xs text-gray-400 font-normal">({count})</span></span>
                                {comp1Val > 0 && (
                                  <span className={`text-xs ${diff >= 0 ? (isNegativeZone ? 'text-red-500' : 'text-emerald-500') : (isNegativeZone ? 'text-emerald-500' : 'text-red-500')}`}>
                                    {diff > 0 ? '+' : ''}{diff}%
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="h-2 rounded-full transition-all duration-1000" style={{ width: `${val}%`, backgroundColor: color }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Attention Areas */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-base font-bold text-gray-900 mb-1">Attention Areas</h2>
                    <p className="text-sm text-gray-500 mb-5">Categories needing focus — with last month comparison</p>
                    {analytics.attentionAreas?.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.attentionAreas.map((area, idx) => (
                          <div key={idx} className="p-4 rounded-xl border border-red-100 bg-red-50/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded-full bg-red-100 text-red-500 flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                                <span className="font-semibold text-gray-800 text-sm">{area.category}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-red-600">{area.score}</div>
                                <div className="text-xs text-gray-400">/ 100</div>
                              </div>
                            </div>
                            {area.lastMonthScore !== undefined && area.lastMonthScore !== null && area.change !== undefined && area.change !== null && (
                              <div className="mt-2 flex items-center gap-2">
                                <span className="text-xs text-gray-400">Last month: {area.lastMonthScore}</span>
                                <div className={`flex items-center gap-0.5 text-xs font-bold ${
                                  area.change >= 0 ? 'text-emerald-600' : 'text-red-500'
                                }`}>
                                  {area.change >= 0 ? <FiArrowUp size={10}/> : <FiArrowDown size={10}/>}
                                  {Math.abs(area.change)} pts
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No attention areas detected.</div>
                    )}
                  </div>
                </div>

                {/* Grade Hotspots + Class Hotspots */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Grade Hotspots */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-base font-bold text-gray-900 mb-1">Grade Hotspots</h2>
                    <p className="text-sm text-gray-500 mb-5">Grades with lowest wellbeing scores</p>
                    {analytics.gradeHotspots?.length > 0 ? (
                      <div className="space-y-0 divide-y divide-gray-50">
                        <div className="grid grid-cols-2 pb-2 text-xs font-bold text-gray-400 uppercase tracking-wide">
                          <span>Grade</span><span className="text-right">Trend</span>
                        </div>
                        {analytics.gradeHotspots.map((h, idx) => (
                          <div key={idx} className="grid grid-cols-2 items-center py-3">
                            <span className="text-sm font-semibold text-gray-800">{h.grade}</span>
                            <div className={`flex items-center justify-end gap-1 text-xs font-bold ${
                              (h.trend || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'
                            }`}>
                              {(h.trend || 0) >= 0 ? <FiArrowUp size={11}/> : <FiArrowDown size={11}/>}
                              {h.trend !== null ? `${Math.abs(h.trend)} pts` : '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No grade data available.</div>
                    )}
                  </div>

                  {/* Class Hotspots */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-base font-bold text-gray-900 mb-1">Class Hotspots</h2>
                    <p className="text-sm text-gray-500 mb-5">Classes with lowest wellbeing scores</p>
                    {analytics.hotspots?.length > 0 ? (
                      <div className="space-y-0 divide-y divide-gray-50">
                        <div className="grid grid-cols-2 pb-2 text-xs font-bold text-gray-400 uppercase tracking-wide">
                          <span>Class</span><span className="text-right">Trend</span>
                        </div>
                        {analytics.hotspots.map((h, idx) => (
                          <div key={idx} className="grid grid-cols-2 items-center py-3">
                            <span className="text-sm font-semibold text-gray-800">{h.class}</span>
                            <div className={`flex items-center justify-end gap-1 text-xs font-bold ${
                              (h.trend || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'
                            }`}>
                              {(h.trend || 0) >= 0 ? <FiArrowUp size={11}/> : <FiArrowDown size={11}/>}
                              {h.trend !== null ? `${Math.abs(h.trend)} pts` : '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-32 flex items-center justify-center text-gray-400 text-sm">No class hotspot data.</div>
                    )}
                  </div>
                </div>
                
              </>
            ) : (
              <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 flex flex-col items-center justify-center text-center mt-6 min-h-[400px]">
                <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                  <FiActivity size={40} className="text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">No Analytics Data Available</h3>
                <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
                  There is currently no analytics data available for the selected period. As students interact with Ensightly and log their wellbeing, rich insights and trends will appear here.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === "Wellbeing Analysis" && (
        <WellbeingAnalysis />
      )}

      {activeTab === "Benchmarking" && (
        <Benchmarking />
      )}

      {activeTab === "School Influence Benchmark" && (
        <SchoolOrbit />
      )}
    </div>
  );
};

export default SchoolDashboard;

